/**
 * How many messages a day is a plausible thing to have asked for.
 *
 * The floor is one rather than zero. "Reach me zero times a day" is
 * indistinguishable, from inside the system and from the learner's side, from a
 * schedule that has quietly broken — and the product already has a way to stop
 * being taught, which is closing or shelving the objective. Accepting zero would
 * leave somebody waiting in silence believing a plan was running.
 *
 * The ceiling is one an hour across a normal waking window. Past that this is not
 * a tutor, it is a notification storm — and the sidecar is an unofficial client
 * on a number that can be banned, where outbound volume is precisely what gets a
 * number banned. See spec.md, "Known gaps".
 */
export const MIN_DAILY_LIMIT = 1;
export const MAX_DAILY_LIMIT = 24;
/**
 * What a learner gets before they have said anything.
 *
 * Not written to the table on signup: an absent `WINDOW` row and a row holding
 * exactly these values mean different things, and the screen says "these are the
 * defaults" only while nothing is stored.
 */
export const DEFAULT_WINDOW = {
    dailyLimit: 6,
    reachFrom: '08:00',
    reachUntil: '21:00',
    explanations: true,
};
/** Minutes in a day, which is the range a local clock time maps into. */
const MINUTES_PER_DAY = 24 * 60;
const DAY_MS = 86_400_000;
/**
 * A local clock time as minutes since local midnight.
 *
 * Strict about the shape on the way in, and the strictness is the point: this
 * string is compared against a wall clock, so `0800`, `8`, `8pm` and `08:00 AM`
 * all have to be either understood exactly or refused. Guessing at one of them
 * produces a window that is out by twelve hours and looks fine in the form.
 *
 * A trailing `:SS` is accepted and the seconds are thrown away. That is the one
 * place clamping is right here: a reachability boundary is a minute-precision
 * idea, the only thing that ever emits a seconds field is a browser's `step`
 * attribute rather than a learner, and refusing `08:00:00` would fail a form that
 * is asking for exactly what we support.
 */
const CLOCK = /^([01][0-9]|2[0-3]):([0-5][0-9])(?::[0-5][0-9])?$/;
export function parseClock(value) {
    if (typeof value !== 'string')
        return undefined;
    const match = CLOCK.exec(value.trim());
    if (!match)
        return undefined;
    return Number(match[1]) * 60 + Number(match[2]);
}
/** The canonical form, so what comes back from a save is what will be compared later. */
export function formatClock(minuteOfDay) {
    const wrapped = ((minuteOfDay % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    const hour = Math.floor(wrapped / 60);
    return `${String(hour).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}
/** How long the learner is reachable each day, in minutes. Never zero for a valid window. */
export function reachableMinutes(window) {
    const from = parseClock(window.reachFrom);
    const until = parseClock(window.reachUntil);
    if (from === undefined || until === undefined)
        return 0;
    return Math.max(0, until - from);
}
/**
 * Whether a stored window is one this file can act on.
 *
 * **Refused, not clamped, and that is the whole design of this function.** A
 * schedule the system quietly adjusted is one the learner did not choose, and the
 * consequence is not cosmetic: it is a message arriving in the middle of the
 * night from a service they explicitly told the hours for. So a limit of zero, a
 * limit nobody could have meant, an unreadable time and a zero-length window are
 * all answers of "no", handed back with which one it was.
 *
 * The two things it deliberately does *not* do:
 *
 * It does not cross-check the cap against the length of the window. "Twenty a day
 * inside one hour" is coherent — the cap is a ceiling, not a spacing — and
 * refusing it would refuse a combination somebody is halfway through editing.
 *
 * It does not consider a window that wraps midnight. See below.
 */
export function validateWindow(input) {
    if (typeof input !== 'object' || input === null)
        return { ok: false, problem: 'malformed' };
    const raw = input;
    const limit = Number(raw['dailyLimit']);
    if (!Number.isFinite(limit))
        return { ok: false, problem: 'malformed' };
    // Refused rather than rounded. The control cannot produce 6.5, so a fractional
    // limit came from something that does not know what it is setting, and rounding
    // it would store a number nobody typed.
    if (!Number.isInteger(limit))
        return { ok: false, problem: 'limit_not_whole' };
    if (limit < MIN_DAILY_LIMIT || limit > MAX_DAILY_LIMIT) {
        return { ok: false, problem: 'limit_out_of_range' };
    }
    const from = parseClock(raw['reachFrom']);
    const until = parseClock(raw['reachUntil']);
    if (from === undefined || until === undefined)
        return { ok: false, problem: 'time_unreadable' };
    if (from === until)
        return { ok: false, problem: 'window_empty' };
    /*
     * A window that wraps midnight is refused outright rather than supported.
     *
     * It is refused *here*, on the way in, because the alternative is computing
     * something silently wrong: every branch below assumes the window opens and
     * closes on the same local day, and handed 21:00–02:00 it would answer "closed
     * since 02:00 this morning, try tomorrow at 21:00" — which is not wrong-looking
     * enough for anybody to notice.
     *
     * It is refused rather than implemented because the daily cap and a wrapping
     * window contradict each other. "Today" for the cap is a calendar day in the
     * learner's zone, so a single unbroken stretch of being reachable from 21:00 to
     * 02:00 straddles two budgets: six messages before midnight and six more after
     * it, twelve in five hours, from a learner who asked for six a day. Fixing that
     * means the cap stops being per day, which is a bigger change than this feature
     * and not one the learner asked for.
     *
     * The honest workaround costs a minute of somebody's evening and is offered in
     * the copy: end the window at 23:59.
     */
    if (until < from)
        return { ok: false, problem: 'window_wraps_midnight' };
    if (typeof raw['explanations'] !== 'boolean')
        return { ok: false, problem: 'malformed' };
    return {
        ok: true,
        window: {
            dailyLimit: limit,
            // Re-emitted in canonical form so a save and a later read compare equal,
            // rather than `08:00` and `08:00:00` being two spellings of one window.
            reachFrom: formatClock(from),
            reachUntil: formatClock(until),
            explanations: raw['explanations'],
        },
    };
}
/**
 * One formatter per zone, kept.
 *
 * `Intl.DateTimeFormat` is expensive to construct relative to what it does here,
 * and resolving one instant takes several reads — so the due sweep, which asks
 * these questions once per learner per pass, would otherwise spend most of its
 * time building formatters it is about to throw away.
 */
const FORMATTERS = new Map();
function formatter(timeZone) {
    const cached = FORMATTERS.get(timeZone);
    if (cached)
        return cached;
    const built = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        // `h23` rather than `hour12: false`, which reports midnight as hour 24 in some
        // engines — and a 24 parsed as an hour lands the whole calculation a day out.
        hourCycle: 'h23',
    });
    FORMATTERS.set(timeZone, built);
    return built;
}
/**
 * Whether a zone name is one the platform knows.
 *
 * Worth asking before anything else, because `Intl` throws a `RangeError` on an
 * unknown zone and a learner row carrying a typo would otherwise surface as a 500
 * from the due sweep rather than as "we do not know where you are".
 */
export function isKnownTimeZone(value) {
    if (typeof value !== 'string' || value.trim() === '')
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
function wallClock(at, timeZone) {
    const parts = formatter(timeZone).formatToParts(at);
    const read = (type) => {
        const part = parts.find((candidate) => candidate.type === type);
        if (!part)
            throw new RangeError(`${timeZone} produced no ${type}`);
        return Number(part.value);
    };
    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        hour: read('hour'),
        minute: read('minute'),
        second: read('second'),
    };
}
/** The zone's offset from UTC at one instant, in milliseconds. Positive east of Greenwich. */
function offsetMillis(at, timeZone) {
    const wall = wallClock(at, timeZone);
    const asIfUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
    // The instant is truncated to the second to match, because `Date.UTC` above
    // carries no milliseconds. Comparing against the untruncated instant makes every
    // offset come back up to 999ms short, and every instant derived from one drifts
    // by that much in the same direction.
    return asIfUtc - Math.floor(at.getTime() / 1000) * 1000;
}
/** The calendar date it is, where the learner is. */
export function localDate(at, timeZone) {
    const wall = wallClock(at, timeZone);
    return { year: wall.year, month: wall.month, day: wall.day };
}
/** `YYYY-MM-DD` in the learner's zone. The bucket a day's worth of anything is counted in. */
export function localDayKey(at, timeZone) {
    const date = localDate(at, timeZone);
    return `${String(date.year).padStart(4, '0')}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
}
/**
 * Civil date arithmetic — no instant involved, so no offset can get into it.
 *
 * This is why "tomorrow" below is computed as a date and then resolved to an
 * instant, rather than as an instant plus a day. The second form is wrong twice a
 * year and right the rest of the time, which is the hardest kind of wrong to find.
 */
export function addLocalDays(date, days) {
    const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
    return {
        year: shifted.getUTCFullYear(),
        month: shifted.getUTCMonth() + 1,
        day: shifted.getUTCDate(),
    };
}
/**
 * The instant at which a given zone's clock reads a given date and time.
 *
 * The inverse of reading a wall clock, and it has no closed form: the offset to
 * subtract is the offset *in force at the answer*, which is what we are solving
 * for. So both plausible offsets are tried — sampled a day either side, which
 * brackets the answer because no zone is more than fourteen hours from UTC and no
 * two transitions are less than a day apart — and each candidate is checked by
 * reading the clock back at it.
 *
 * That check is what makes the two hard days of the year correct rather than
 * plausible.
 *
 * **A local time that happens twice** — the hour repeated when clocks go back —
 * has two valid candidates, and this takes the earlier. It is the first instant at
 * which the learner said they were reachable; taking the later one would hold the
 * window shut for an hour the learner had offered, on one day, for no reason they
 * could see.
 *
 * **A local time that does not exist** — the hour skipped when clocks go forward —
 * has none, and this moves *forward* past the gap, to the same wall time plus the
 * length of the skip. Forward rather than backward because backward means sending
 * inside the hours somebody declared quiet, and forward rather than refusing
 * because refusing means a learner silently gets no messages on the day the clocks
 * change. It also preserves the elapsed length of the window: a 01:00–02:30 window
 * on a day that skips 02:00–03:00 runs 01:00 to 03:30, which is still ninety
 * minutes of being reachable.
 */
function instantAtLocalTime(date, minuteOfDay, timeZone) {
    const hour = Math.floor(minuteOfDay / 60);
    const minute = minuteOfDay % 60;
    const asIfUtc = Date.UTC(date.year, date.month - 1, date.day, hour, minute);
    const candidates = [
        ...new Set([
            asIfUtc - offsetMillis(new Date(asIfUtc - DAY_MS), timeZone),
            asIfUtc - offsetMillis(new Date(asIfUtc + DAY_MS), timeZone),
        ]),
    ].sort((a, b) => a - b);
    const exact = candidates.filter((candidate) => {
        const wall = wallClock(new Date(candidate), timeZone);
        return (wall.year === date.year &&
            wall.month === date.month &&
            wall.day === date.day &&
            wall.hour === hour &&
            wall.minute === minute);
    });
    // The earliest reading that matches; failing that the latest candidate, which is
    // the one computed with the pre-transition offset and therefore the one that
    // lands after the gap rather than before it.
    const chosen = exact[0] ?? candidates[candidates.length - 1];
    if (chosen === undefined)
        throw new RangeError(`no instant resolves in ${timeZone}`);
    return new Date(chosen);
}
/**
 * The instants a learner's day begins and ends.
 *
 * `start` is inclusive, `end` exclusive, and `end` is the *next* day's start
 * rather than a fixed twenty-four hours later — which is the difference between a
 * correct range and one that loses or double-counts an hour twice a year.
 *
 * This is what "messages already sent today" is counted between. Counting against
 * UTC days instead resets a Costa Rican learner's daily budget at six in the
 * evening, so an evening's messages are followed by a fresh allowance before bed
 * and the cap they chose is quietly nearly doubled.
 */
export function localDayRange(at, timeZone) {
    const today = localDate(at, timeZone);
    return {
        start: instantAtLocalTime(today, 0, timeZone),
        end: instantAtLocalTime(addLocalDays(today, 1), 0, timeZone),
    };
}
/**
 * The next instant at which this learner may be sent something.
 *
 * `sentToday` is a count over the learner's own day — see `localDayRange`, and use
 * it to produce the count rather than counting some other way.
 *
 * Four answers, and the ordering between them matters. The daily cap is checked
 * **first**, because a learner who has had their six messages is done for the day
 * even though the window is still open: answering "now" there would ignore the one
 * number they most obviously chose. The rest is the window — before it, inside it,
 * past it.
 *
 * Throws on a window this file cannot act on. Every path that writes one validates
 * it first, so an unparseable window here means a row that arrived by some other
 * route; defaulting to 08:00–21:00 at that point would send messages at hours the
 * learner never chose, which is exactly the failure this file exists to prevent.
 * An unknown timezone throws too, out of `Intl`, for the same reason.
 */
export function nextSendTime(window, timeZone, sentToday, now) {
    const checked = validateWindow(window);
    if (!checked.ok) {
        throw new RangeError(`unusable reach window: ${checked.problem}`);
    }
    const from = parseClock(checked.window.reachFrom);
    const until = parseClock(checked.window.reachUntil);
    const today = localDate(now, timeZone);
    const tomorrowOpens = instantAtLocalTime(addLocalDays(today, 1), from, timeZone);
    if (sentToday >= checked.window.dailyLimit) {
        return { at: tomorrowOpens, today: false, reason: 'limit_reached' };
    }
    const opens = instantAtLocalTime(today, from, timeZone);
    if (now.getTime() < opens.getTime()) {
        return { at: opens, today: true, reason: 'before_window' };
    }
    // Exclusive at the close, inclusive at the open: a message at exactly the closing
    // minute is outside the hours the learner gave, and one at the opening minute is
    // inside them.
    const closes = instantAtLocalTime(today, until, timeZone);
    if (now.getTime() >= closes.getTime()) {
        return { at: tomorrowOpens, today: false, reason: 'after_window' };
    }
    return { at: new Date(now.getTime()), today: true, reason: 'open' };
}
//# sourceMappingURL=time.js.map